import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BellIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import BookPromotionalGraphics from '../../components/Books/BookPromotionalGraphics';
import { samplePromotionalGraphics } from '../../data/samplePromotionalGraphics';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import { BookCover } from './BookCover';
import { BookInfo } from './BookInfo';
import { BookDetailsCard } from './BookDetailsCard';
import SeriesSection from './SeriesSection';
import ContentCategories from './ContentCategories';
import RelatedSection from './RelatedSection';
import LatestReviews from './LatestReviews';
import AvailableEditions from './AvailableEditions';
import { allBooks } from '../../data/booksData';
import { aftertideBooks } from '../../data/aftertideBooksData';
import { useAfterTideFilter } from '../../hooks/useAfterTideFilter';
import { applyEditionToBook, getEditionById, getLinkedStoryForBook } from '../../data/linkedEditions';
export default function BookDetail({
  setCartItemCount
}: {
  setCartItemCount: React.Dispatch<React.SetStateAction<number>>;
}) {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const {
    platformData
  } = useAfterTideFilter();
  // Find book in appropriate dataset
  const baseBooks = platformData(allBooks, aftertideBooks);
  const baseBook = baseBooks.find(b => b.id === id) || baseBooks[0];

  // Linked editions. Undefined for every single-edition book, which keeps the
  // page byte-for-byte unchanged for them.
  const linkedStory = getLinkedStoryForBook(baseBook.id);
  // Held in the URL so a chosen edition can be linked to and shared — the
  // author profile links straight to a specific edition this way.
  const selectedEditionId = searchParams.get('edition');
  const selectedEdition = linkedStory ? getEditionById(linkedStory, selectedEditionId) : undefined;

  // Edition metadata is merged OVER the catalogue entry, so every section
  // component below keeps reading the field names it always has.
  const book = linkedStory && selectedEdition ? applyEditionToBook(baseBook, selectedEdition) : baseBook;
  const handleSelectEdition = (editionId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('edition', editionId);
    setSearchParams(next, {
      replace: true
    });
  };
  const [activeTab, setActiveTab] = useState('overview');
  // Safely handle missing formats array for AfterTide books
  const defaultFormat = book.formats && book.formats.length > 0 ? book.formats[0] : {
    type: 'eBook',
    price: book.price || 4.99,
    available: true
  };
  const [selectedFormat, setSelectedFormat] = useState(defaultFormat);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const bookId = book.id;
  const bookTitle = book.title;
  const authorId = 'elena-richards';
  const authorName = book.author;
  const bookGraphics = samplePromotionalGraphics.filter(graphic => graphic.bookId === '1').map(({
    bookId,
    bookTitle,
    authorId,
    authorName,
    ...rest
  }) => rest);
  return <div className={dk ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'} data-id="element-7363" data-mp="1f0gv12sgtci6">
      {/* Pre-Release Banner */}
      {book.coming_soon && <div className={`${dk ? 'bg-amber-900/30 border-amber-800' : 'bg-amber-50 border-amber-100'} border-b py-3`} data-id="element-7364" data-mp="1js8deq1gwbx8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left" data-id="element-7365" data-mp="racux5ehjuvx">
            <div className="flex items-center gap-3" data-id="element-7366" data-mp="2t01v6c1grqss">
              <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-bold bg-amber-500 text-white uppercase tracking-wide`} data-id="element-7367" data-mp="2o17cda3hy28o">
                Coming Soon
              </span>
              <span className={`font-medium ${dk ? 'text-amber-200' : 'text-amber-900'}`} data-id="element-7368" data-mp="2xxah8npqgezb">
                Releasing {book.publishDate}
              </span>
            </div>
            <button className={`inline-flex items-center text-sm font-medium transition-colors ${dk ? 'text-amber-400 hover:text-amber-300' : 'text-amber-700 hover:text-amber-900'}`} data-id="element-7369" data-mp="37oethfo0xlqg">
              <BellIcon className="w-4 h-4 mr-2" data-id="element-7370" data-mp="3gn9yiofc0ps6" />
              Get notified when this book releases
            </button>
          </div>
        </div>}

      {/* Book Header */}
      <div className={`${dk ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} py-6 sm:py-8 border-b`} data-id="element-7371" data-mp="1mcwoipea810h">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7372" data-mp="6se4obvrgzd0">
          <div className="mb-6" data-id="element-7373" data-mp="6rm93125z1mq">
            <BackButton data-id="element-7374" data-mp="3o8vnwpa1corn" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8" data-id="element-7375" data-mp="3o7ufytobgie5">
            <BookCover book={book} isAudioPlaying={isAudioPlaying} setIsAudioPlaying={setIsAudioPlaying} data-id="element-7376" data-mp="3t7gowqx43ch5" />
            <BookInfo book={book} selectedFormat={selectedFormat} setSelectedFormat={setSelectedFormat} setCartItemCount={setCartItemCount} data-id="element-7377" data-mp="3faab7ar8w3ys" />
          </div>

          {/* Available Editions — sits beside the cover and metadata it
              controls, and is absent entirely for single-edition books. */}
          {linkedStory && selectedEdition && <div className="mt-6" data-id="element-7378" data-mp="18hhm0vuxbtle">
              <AvailableEditions story={linkedStory} selectedEditionId={selectedEdition.id} onSelectEdition={handleSelectEdition} data-id="element-7379" data-mp="18gojk72zzq92" />
            </div>}
        </div>
      </div>

      {/* Book Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-7380" data-mp="x1v35jb4tnz9">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-7381" data-mp="x47s4gfb4ylt">
          <BookDetailsCard book={book} data-id="element-7382" data-mp="t2dvcj88geqx" />
          <div className="md:col-span-2" data-id="element-7383" data-mp="1pr2a96onjlw4">
            <SeriesSection book={book} data-id="element-7384" data-mp="1pq0ni4y9xayk" />
            <ContentCategories book={book} expandedSection={expandedSection} setExpandedSection={setExpandedSection} data-id="element-7385" data-mp="1pt6fpt278syi" />
            <RelatedSection book={book} data-id="element-7386" data-mp="1fzpi0dyxytpk" />
          </div>
        </div>
      </div>

      {/* Promotional Graphics */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7387" data-mp="1bxks9qbm6h87">
        <div className="mt-12" data-id="element-7388" data-mp="28ilax6v7enuz">
          <BookPromotionalGraphics bookId={bookId} bookTitle={bookTitle} authorId={authorId} authorName={authorName} graphics={bookGraphics} data-id="element-7389" data-mp="28jo2zi49iwch" />
        </div>
      </div>

      {/* Latest Reviews */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7390" data-mp="3rdpmeizdfpef">
        <LatestReviews book={book} data-id="element-7391" data-mp="3vej7l4lfiq3m" />
      </div>
    </div>;
}